
import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  schema?: object | object[];
}

export function useSEO({ title, description, keywords, canonical, schema }: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    
    // Open Graph
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    if (canonical) updateMetaTag('og:url', canonical, true);

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Canonical URL
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.rel = 'canonical';
        document.head.appendChild(linkElement);
      }
      linkElement.href = canonical;
    }

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Schema.org JSON-LD - Remove existing dynamic schemas
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
    existingSchemas.forEach(script => script.remove());

    // Add new schema(s)
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach(schemaObj => {
        const scriptElement = document.createElement('script');
        scriptElement.type = 'application/ld+json';
        scriptElement.setAttribute('data-dynamic', 'true');
        scriptElement.textContent = JSON.stringify(schemaObj);
        document.head.appendChild(scriptElement);
      });
    }

    return () => {
      // Cleanup dynamic schemas on unmount
      const dynamicSchemas = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
      dynamicSchemas.forEach(script => script.remove());
    };
  }, [title, description, keywords, canonical, schema]);
}

export function generateOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'POWERENO',
    legalName: 'POWERENO SAS',
    url: siteUrl,
    logo: `${siteUrl}/vite.svg`,
    description: 'Spécialiste de la rénovation LED haute performance pour bâtiments tertiaires en France',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '37 rue d\'Amsterdam',
      addressLocality: 'Paris',
      postalCode: '75008',
      addressCountry: 'FR'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@powereno.fr',
      contactType: 'customer service',
      areaServed: 'FR',
      availableLanguage: 'French'
    },
    sameAs: []
  };
}

export function generateWebSiteSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'POWERENO',
    url: siteUrl,
    description: 'Spécialiste de la rénovation LED haute performance pour bâtiments tertiaires',
    publisher: {
      '@type': 'Organization',
      name: 'POWERENO'
    }
  };
}

export function generateWebPageSchema(pageName: string, description: string, url?: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageName,
    description: description,
    url: url || siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'POWERENO',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/vite.svg`
      }
    }
  };
}

export function generateProductSchema(product: {
  name: string;
  description: string;
  image: string;
  brand?: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    brand: {
      '@type': 'Brand',
      name: product.brand || 'POWERENO'
    },
    manufacturer: {
      '@type': 'Organization',
      name: 'POWERENO'
    },
    url: `${siteUrl}/products`
  };
}

export function generateServiceSchema(service: {
  name: string;
  description: string;
  provider?: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: service.provider || 'POWERENO',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '37 rue d\'Amsterdam',
        addressLocality: 'Paris',
        postalCode: '75008',
        addressCountry: 'FR'
      }
    },
    areaServed: {
      '@type': 'Country',
      name: 'France'
    },
    url: `${siteUrl}/services`
  };
}

export function generateItemListSchema(items: Array<{ name: string; description: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      description: item.description,
      url: item.url
    }))
  };
}

export function generateContactPageSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Demande d\'étude gratuite',
    description: 'Contactez POWERENO pour une étude gratuite de votre projet de rénovation LED',
    url: `${siteUrl}/study`,
    mainEntity: {
      '@type': 'Organization',
      name: 'POWERENO',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@powereno.fr',
        contactType: 'customer service',
        areaServed: 'FR',
        availableLanguage: 'French'
      }
    }
  };
}
